/**
 * /admin/ingest/anomalies — N4 — canonical match anomaly queue.
 * Unresolved anomalies block matches from entering the training corpus.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw, AlertTriangle, ArrowLeft, Layers, CheckCircle2, X, Loader2,
} from "lucide-react";
import {
  listAnomalies, bulkResolveAnomalies, bulkIgnoreAnomalies,
} from "../../api/ingest.admin.api";
import ConfirmDialog from "../../components/common/ConfirmDialog";

const SEVERITY_STYLES = {
  low:    "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30",
  medium: "bg-amber-500/10 text-amber-300 border border-amber-500/30",
  high:   "bg-red-500/10 text-red-300 border border-red-500/30",
};

const STATUS_OPTIONS = [
  { value: "open",     label: "Open (actionable)" },
  { value: "resolved", label: "Resolved" },
  { value: "ignored",  label: "Ignored" },
  { value: "all",      label: "All" },
];

export default function AdminIngestAnomalies() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open");
  const [sevFilter, setSevFilter] = useState("");
  const [checkFilter, setCheckFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [toast, setToast] = useState("");
  // "resolve" | "ignore" | null — drives the ConfirmDialog
  const [bulkPending, setBulkPending] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, sevFilter, checkFilter]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await listAnomalies({
        status: statusFilter || undefined,
        severity: sevFilter || undefined,
        check_name: checkFilter || undefined,
      });
      setRows(data.results || []);
      setCount(data.count || 0);
      setSelectedIds(new Set());
    } finally { setLoading(false); }
  }

  const allChecked = useMemo(
    () => rows.length > 0 && rows.every((r) => selectedIds.has(r.id)),
    [rows, selectedIds],
  );

  function toggle(id) {
    setSelectedIds((p) => {
      const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n;
    });
  }
  function selectAll(c) { setSelectedIds(c ? new Set(rows.map((r) => r.id)) : new Set()); }

  async function doBulk(kind) {
    if (!selectedIds.size || bulkBusy) return;
    setBulkBusy(true);
    try {
      const fn = kind === "resolve" ? bulkResolveAnomalies : bulkIgnoreAnomalies;
      const { data } = await fn([...selectedIds]);
      setToast(`${kind === "resolve" ? "Resolved" : "Ignored"} ${data[kind === "resolve" ? "resolved" : "ignored"]}`);
      setSelectedIds(new Set());
      setBulkPending(null);
      load();
    } catch (e) {
      setToast(`Bulk action failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div className="p-6 space-y-4">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Anomaly queue</h1>
          <p className="text-sm text-text-secondary mt-1">
            {count.toLocaleString("en-IN")} anomaly row(s). Unresolved entries
            BLOCK their match from entering the training corpus until cleared.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <>
              <button onClick={() => setBulkPending("resolve")} className="px-3 py-2 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-500 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" /> Resolve {selectedIds.size}
              </button>
              <button onClick={() => setBulkPending("ignore")} className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5">
                <Layers className="w-4 h-4" /> Ignore {selectedIds.size}
              </button>
            </>
          )}
          <button onClick={load} className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      <div className="flex items-center gap-3 flex-wrap text-sm">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-1.5 rounded-md border border-border-default bg-bg-primary">
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={sevFilter} onChange={(e) => setSevFilter(e.target.value)} className="px-3 py-1.5 rounded-md border border-border-default bg-bg-primary">
          <option value="">All severities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <input value={checkFilter} onChange={(e) => setCheckFilter(e.target.value)} placeholder="check_name filter (e.g. wicket_count_exceeds_10)" className="px-3 py-1.5 rounded-md border border-border-default bg-bg-primary w-72 text-xs font-mono" />
      </div>

      {toast && (
        <div className="rounded-md bg-blue-50 text-blue-700 px-4 py-2 text-sm flex items-center justify-between">
          <span><CheckCircle2 className="w-4 h-4 inline-block mr-1.5" />{toast}</span>
          <button onClick={() => setToast("")}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="rounded-md border border-border-default bg-bg-primary overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Ingest anomalies — current filter</caption>
          <thead className="bg-bg-secondary text-left text-xs uppercase tracking-wider text-text-secondary">
            <tr>
              <th scope="col" className="px-4 py-2.5 w-10">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => selectAll(e.target.checked)}
                  aria-label="Select all visible anomalies"
                />
              </th>
              <th scope="col" className="px-4 py-2.5">Match</th>
              <th scope="col" className="px-4 py-2.5">Check</th>
              <th scope="col" className="px-4 py-2.5">Severity</th>
              <th scope="col" className="px-4 py-2.5">Detail</th>
              <th scope="col" className="px-4 py-2.5">Status</th>
              <th scope="col" className="px-4 py-2.5">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-default">
            {loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-text-secondary">
                <Loader2 className="w-5 h-5 inline-block mr-2 animate-spin" /> Loading…
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-text-secondary">
                {statusFilter === "open" ? "🎉 No open anomalies." : "No rows match these filters."}
              </td></tr>
            )}
            {rows.map((r) => {
              const isOpen = !r.resolved_at && !r.ignored_at;
              return (
                <tr key={r.id} className="hover:bg-bg-secondary">
                  <td className="px-4 py-2">
                    {isOpen && (
                      <input type="checkbox" checked={selectedIds.has(r.id)} onChange={() => toggle(r.id)} />
                    )}
                  </td>
                  <td className="px-4 py-2 font-medium">{r.match_name || "(unnamed)"}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.check_name}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${SEVERITY_STYLES[r.severity] || ""}`}>
                      {r.severity}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-text-secondary max-w-md truncate" title={r.detail}>{r.detail}</td>
                  <td className="px-4 py-2 text-xs">
                    {r.resolved_at ? <span className="text-emerald-300">RESOLVED</span>
                      : r.ignored_at ? <span className="text-text-secondary">IGNORED</span>
                      : <span className="text-amber-300">OPEN</span>}
                  </td>
                  <td className="px-4 py-2 text-xs text-text-secondary">
                    {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={bulkPending !== null}
        title={
          bulkPending === "resolve"
            ? `Resolve ${selectedIds.size} anomaly row(s)?`
            : `Ignore ${selectedIds.size} anomaly row(s)?`
        }
        body={
          bulkPending === "resolve"
            ? "Marks the data fix as applied. Affected matches re-enter the training corpus on next cycle."
            : "Marks as false positive. Won't BLOCK matches, but the rows stay visible under the Ignored filter."
        }
        confirmLabel={
          bulkPending === "resolve"
            ? `Resolve ${selectedIds.size}`
            : `Ignore ${selectedIds.size}`
        }
        tone={bulkPending === "ignore" ? "danger" : "primary"}
        busy={bulkBusy}
        onConfirm={() => doBulk(bulkPending)}
        onCancel={() => setBulkPending(null)}
      />
    </div>
  );
}
